import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AccountButton from "../AccountButton";
import PaymentModal from "../PaymentModal";
import { FetchQRCode, getPatientCard } from "../api/apiFunctions";
import { t } from "../i18n";
import { sortByDisplayOnCardAndExtractNames } from "../utils";
import SalMineCardPrint from "./SalMineCardPrint";

export default function SalMineCardPage({ lang = "en", goToTab }) {
  const navigate = useNavigate();
  const [showPrint, setShowPrint] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'print' or 'download'
  const cardRef = useRef(null);

  // State for real card data
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  // Default QR code URL (fallback)
  const defaultQrUrl =
    "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=example";

  useEffect(() => {
    window.scrollTo(0, 0);

    // Add listener to hide print card after printing
    const handleAfterPrint = () => {
      setShowPrint(false);
    };

    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  // Fetch card data on component mount
  useEffect(() => {
    const fetchCardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get token from localStorage
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");

        if (!token) {
          throw new Error(
            "Authentication token not found. Please login again.",
          );
        }

        // Fetch card data from API
        const response = await getPatientCard(token);

        if (response?.success && response?.data) {
          // Transform API data to match the expected format
          const transformedData = {
            name: response.data.patientName,
            dob:
              response.data.dateOfBirth &&
              new Date(response.data.dateOfBirth).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              }),
            ssn: response.data.ssn,
            allergies: sortByDisplayOnCardAndExtractNames(
              response.data.allergies,
            ),
            medications: sortByDisplayOnCardAndExtractNames(
              response.data.medications,
            ),
            conditions: sortByDisplayOnCardAndExtractNames(
              response.data.conditions,
            ),
            hospitals: sortByDisplayOnCardAndExtractNames(
              response.data.hospitals,
            ),
            doctors: sortByDisplayOnCardAndExtractNames(response.data.doctors),
            // contact: response.data.contact,
            religion: response.data.religion,
            updated:
              response.data.updatedAt &&
              new Date(response.data.updatedAt).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              }),
            organDonor: response.data.organDonor,
            bloodType: response.data.bloodType,
            qrUrl: qrCodeUrl, // Will be updated with real QR code
            // Emergency contact details
            emergencyContactName:
              response.data.emergencyContacts[0]?.name || "",
            emergencyContactPhone:
              response.data.emergencyContacts[0]?.phone || "",
            emergencyRelation:
              response.data.emergencyContacts[0]?.relation || "",
            // Additional fields from API
            email: response.data.email,
            primaryPhoneNumber: response.data.primaryPhoneNumber,
            secondaryPhoneNumber: response.data.secondaryPhoneNumber,
            secondaryMobileNumber: response.data.secondaryMobileNumber,
            primaryInsurance: response.data.primaryInsurance,
            secondaryInsurance: response.data.secondaryInsurance,
            ethnicity: response.data.ethnicity,
            age: response.data.age,
            ekg: response.data.ekg,
            cardId: response.data.cardId,
            createdAt: response.data.createdAt,
            updatedAt: response.data.updatedAt,
          };

          setCardData(transformedData);

          // Fetch QR code after card data is loaded
          try {
            const qrResponse = await FetchQRCode(token);
            if (qrResponse.success && qrResponse.data) {
              setQrCodeUrl(
                qrResponse.data.qrCodeData ||
                  qrResponse.data.url ||
                  qrResponse.data,
              );
            }
          } catch (qrError) {
            console.error("Error fetching QR code:", qrError);
            // Keep using default QR code if QR fetch fails
          }
        } else {
          throw new Error("Failed to fetch card data");
        }
      } catch (error) {
        console.error("Error fetching card data:", error);
        setError(
          error.message || "Failed to load card data. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCardData();
  }, []);

  const handleDownload = () => {
    setPendingAction("download");
    setShowPaymentModal(true);
  };

  const handlePrint = () => {
    setPendingAction("print");
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    setShowPaymentModal(false);
    if (pendingAction === "download") {
      setShowDownload(true);
      setTimeout(async () => {
        const html2canvas = (await import("html2canvas")).default;

        if (cardRef.current) {
          const cardElement = cardRef.current.querySelector(
            ".sal-mine-print-card",
          );

          if (cardElement) {
            try {
              const clonedElement = cardElement.cloneNode(true);
              document.body.appendChild(clonedElement);
              clonedElement.style.position = "absolute";
              clonedElement.style.left = "-9999px";

              const images = clonedElement.querySelectorAll("img");

              for (const img of images) {
                try {
                  const canvas = document.createElement("canvas");
                  const ctx = canvas.getContext("2d");

                  const imageObj = new Image();
                  imageObj.crossOrigin = "anonymous";

                  await new Promise((resolve, reject) => {
                    imageObj.onload = () => {
                      canvas.width = imageObj.naturalWidth || imageObj.width;
                      canvas.height = imageObj.naturalHeight || imageObj.height;
                      ctx.drawImage(imageObj, 0, 0);

                      try {
                        const dataURL = canvas.toDataURL("image/png");
                        img.src = dataURL;
                        resolve();
                      } catch (err) {
                        reject(err);
                      }
                    };
                    imageObj.onerror = reject;
                    imageObj.src =
                      img.src +
                      (img.src.includes("?") ? "&" : "?") +
                      "t=" +
                      Date.now();
                  });
                } catch (error) {
                  console.error("Image conversion failed:", img.src, error);
                }
              }

              await new Promise((resolve) => setTimeout(resolve, 300));

              const canvas = await html2canvas(clonedElement, {
                backgroundColor: null,
                scale: 2,
                width: 350,
                height: 200,
              });

              document.body.removeChild(clonedElement);

              canvas.toBlob((blob) => {
                if (blob) {
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `sal-mine-card-${cardData.name
                    .replace(/\s+/g, "-")
                    .toLowerCase()}.png`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }
              }, "image/png");
            } catch (error) {
              console.error("Error downloading card:", error);
              alert("Failed to download card. Please try again.");
            } finally {
              setShowDownload(false);
            }
          }
        }
      }, 100);
    } else if (pendingAction === "print") {
      // Show the hidden print card and trigger print dialog
      setShowPrint(true);
      setTimeout(() => {
        window.print();
      }, 100);
    }
    setPendingAction(null);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center justify-center pb-8 px-4 sm:px-6">
        <div className="w-full max-w-4xl">
          <div className="bg-white rounded-xl shadow p-4 sm:p-6 md:p-8 mt-4">
            <div className="flex flex-col sm:flex-row items-center justify-center py-8 sm:py-12 gap-4">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[#4962d8]"></div>
              <span className="text-base sm:text-lg text-[#2d348f] text-center">
                Loading your SAL card...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center justify-center pb-8 px-4 sm:px-6">
        <div className="w-full max-w-4xl">
          <div className="bg-white rounded-xl shadow p-4 sm:p-6 md:p-8 mt-4">
            <div className="text-center py-8 sm:py-12">
              <div className="text-red-500 text-base sm:text-lg mb-4 px-4">
                ⚠️ {error}
              </div>
              <AccountButton
                className="bg-[#4962d8] text-white px-6 py-2 rounded text-sm sm:text-base"
                onClick={() => window.location.reload()}
              >
                Try Again
              </AccountButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show no card state
  if (!cardData) {
    return (
      <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center justify-center pb-8 px-4 sm:px-6">
        <div className="w-full max-w-4xl">
          <div className="bg-white rounded-xl shadow p-4 sm:p-6 md:p-8 mt-4">
            <div className="text-center py-8 sm:py-12">
              <div className="text-[#2d348f] text-base sm:text-lg mb-4">
                No SAL card found
              </div>
              <p className="text-gray-600 mb-6 text-sm sm:text-base px-4">
                You need to create your SAL card first.
              </p>
              <AccountButton
                className="bg-[#4962d8] text-white px-6 py-2 rounded text-sm sm:text-base"
                onClick={() => navigate("/signup")}
              >
                Create Your Card
              </AccountButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center pb-8 px-4 sm:px-6">
      <div className="bg-white rounded-xl shadow p-4 sm:p-6 md:p-8 mt-4 max-w-5xl w-full">
        <div className="text-base sm:text-lg font-bold text-[#2d348f] mb-4 mt-2">
          {t("salCard", lang)}
        </div>
        <div className="flex flex-col gap-4 lg:flex-row lg:gap-5 lg:justify-between">
          <SalMineCardPrint
            name={cardData.name}
            dob={cardData.dob}
            ssn={cardData.ssn}
            allergies={cardData.allergies}
            medications={cardData.medications}
            conditions={cardData.conditions}
            hospital={cardData.hospitals}
            doctors={cardData.doctors}
            contact={cardData.contact}
            religion={cardData.religion}
            updated={cardData.updated}
            organDonor={cardData.organDonor}
            bloodType={cardData.bloodType}
            qrUrl={qrCodeUrl || cardData.qrUrl}
            emergencyContactName={cardData.emergencyContactName}
            emergencyContactPhone={cardData.emergencyContactPhone}
            emergencyRelation={cardData.emergencyRelation}
            hidePrintButton={true}
            downloadMode={false}
            ekg={cardData.ekg}
          />
          <div className="flex flex-col gap-3 sm:gap-4 w-full">
            {[
              ["allergies", cardData.allergies],
              ["conditions", cardData.conditions],
              ["medications", cardData.medications],
            ].map(([label, value]) => (
              <div key={label} className="flex flex-col sm:flex-row w-full">
                {/* Label */}
                <div className="flex items-center justify-start px-3 sm:px-4 py-2 sm:py-3 bg-[#f3eaea] text-[#b48a78] font-semibold text-xs sm:text-sm rounded-t-xl sm:rounded-l-xl sm:rounded-t-none min-w-[100px] sm:min-w-[120px] capitalize border border-[#e5e7eb] sm:border-r-0 border-b-0 sm:border-b">
                  {t(label, lang)}
                </div>

                {/* Read-only Dropdown */}
                <div className="bg-white border border-[#e5e7eb] sm:border-l-0 rounded-b-xl sm:rounded-r-xl sm:rounded-b-none w-full">
                  <select className="w-full px-3 sm:px-4 py-2 sm:py-3 text-gray-600 text-xs sm:text-sm bg-white rounded-b-xl sm:rounded-r-xl focus:outline-none cursor-not-allowed">
                    {Array.isArray(value) && value.length > 0 ? (
                      value.map((item, idx) => (
                        <option key={idx} value={item}>
                          {item}
                        </option>
                      ))
                    ) : (
                      <option value="">N/A</option>
                    )}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Card Render Section */}
        <div className="mt-4">
          <div className="text-sm sm:text-base font-bold text-[#2d348f] mb-4">
            {t("cardRender", lang)}
          </div>
          <div className="flex items-center">
            <div className="flex flex-col md:flex-row gap-4 sm:gap-6 items-center mb-8">
              <div className="flex justify-center items-center w-full md:w-auto">
                <div
                  className="bg-white rounded-lg p-3 sm:p-4 border flex items-center justify-center"
                  style={{
                    minWidth: "min(220px, 100%)",
                    minHeight: "min(220px, 100%)",
                    maxWidth: "220px",
                    maxHeight: "220px",
                  }}
                >
                  <img
                    src={qrCodeUrl || defaultQrUrl}
                    alt="QR Code"
                    className="w-32 h-32 sm:w-40 sm:h-40 object-contain"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:gap-4 w-full md:w-72">
                <AccountButton
                  className="w-full bg-gradient-to-r from-[#4962d8] to-[#2d348f] text-white font-semibold text-sm sm:text-base py-2"
                  onClick={handlePrint}
                >
                  {t("printCard", lang)}
                </AccountButton>
                <AccountButton
                  className="w-full bg-gradient-to-r from-[#4962d8] to-[#2d348f] text-white font-semibold text-sm sm:text-base py-2"
                  onClick={handleDownload}
                  disabled={showDownload}
                >
                  {showDownload ? "Downloading..." : t("download", lang)}
                </AccountButton>
              </div>
            </div>
            {/* Hidden Print Card - No Modal */}
            {showPrint && (
              <div
                style={{
                  position: "fixed",
                  top: "-9999px",
                  left: "-9999px",
                  zIndex: -1,
                }}
              >
                <SalMineCardPrint
                  name={cardData.name}
                  dob={cardData.dob}
                  ssn={cardData.ssn}
                  allergies={cardData.allergies}
                  medications={cardData.medications}
                  conditions={cardData.conditions}
                  hospital={cardData.hospitals}
                  doctors={cardData.doctors}
                  contact={cardData.contact}
                  religion={cardData.religion}
                  updated={cardData.updated}
                  organDonor={cardData.organDonor}
                  bloodType={cardData.bloodType}
                  qrUrl={qrCodeUrl || cardData.qrUrl}
                  emergencyContactName={cardData.emergencyContactName}
                  emergencyContactPhone={cardData.emergencyContactPhone}
                  emergencyRelation={cardData.emergencyRelation}
                  hidePrintButton={true}
                  ekg={cardData.ekg}
                />
              </div>
            )}
            {/* Download Modal - Hidden */}
            {showDownload && (
              <div
                style={{
                  position: "fixed",
                  top: "-9999px",
                  left: "-9999px",
                  width: "350px",
                  height: "200px",
                  zIndex: -1,
                  background: "transparent",
                }}
              >
                <div ref={cardRef}>
                  <SalMineCardPrint
                    name={cardData.name}
                    dob={cardData.dob}
                    ssn={cardData.ssn}
                    allergies={cardData.allergies}
                    medications={cardData.medications}
                    conditions={cardData.conditions}
                    hospital={cardData.hospitals}
                    doctors={cardData.doctors}
                    contact={cardData.contact}
                    religion={cardData.religion}
                    updated={cardData.updated}
                    organDonor={cardData.organDonor}
                    bloodType={cardData.bloodType}
                    qrUrl={qrCodeUrl || cardData.qrUrl}
                    emergencyContactName={cardData.emergencyContactName}
                    emergencyContactPhone={cardData.emergencyContactPhone}
                    emergencyRelation={cardData.emergencyRelation}
                    hidePrintButton={true}
                    downloadMode={true}
                    ekg={cardData.ekg}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4">
            <AccountButton
              className="w-full sm:w-1/2 bg-gradient-to-r from-[#4962d8] to-[#2d348f] text-white text-sm sm:text-base"
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> {t("back", lang)}
            </AccountButton>
          </div>
        </div>
      </div>

      <PaymentModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPay={handlePayment}
      />
    </div>
  );
}
